import React, { useState, useMemo } from 'react';
import Hero from '../../components/ui/Hero';
import SupplierFilters from '../../components/suppliers/SupplierFilters';
import SupplierCard from '../../components/suppliers/SupplierCard';
import SupplierCTA from '../../components/suppliers/SupplierCTA';
import { useSuppliers } from '../../hooks/useSuppliers';
import type { SupplierCategory } from '../../types/supplier';

const DirectoryPage = () => {
  const heroImage = "https://nifmkzddenyukgjcdetc.supabase.co/storage/v1/object/public/animatronics_data/logo_light.webp?t=2024-12-30T20%3A06%3A02.198Z";
  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<SupplierCategory[]>([]);
  const { suppliers, isLoading, error } = useSuppliers();

  const categories = useMemo(() => {
    const allCategories = new Set<SupplierCategory>();
    suppliers.forEach(supplier => {
      supplier.categories.forEach(category => {
        allCategories.add(category as SupplierCategory);
      });
    });
    return Array.from(allCategories).sort();
  }, [suppliers]);

  const handleCategoryToggle = (category: SupplierCategory) => {
    setSelectedCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const filteredSuppliers = useMemo(() => {
    return suppliers.filter(supplier => {
      const matchesSearch = search
        ? supplier.company_name.toLowerCase().includes(search.toLowerCase()) ||
          supplier.description.toLowerCase().includes(search.toLowerCase())
        : true;

      const matchesCategories = selectedCategories.length === 0 ||
        selectedCategories.some(category => supplier.categories.includes(category));

      return matchesSearch && matchesCategories;
    });
  }, [suppliers, search, selectedCategories]);

  return (
    <div>
      <Hero
        title="Animatronics Industry Directory"
        description="Showcase Your Business. Discover Top Animatronic Suppliers and Theme Park Solutions."
        backgroundImage={heroImage}
        compact
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0">
        <SupplierFilters
          search={search}
          onSearchChange={setSearch}
          categories={categories}
          selectedCategories={selectedCategories}
          onCategoryToggle={handleCategoryToggle}
        />

        <div className="space-y-6">
          {isLoading ? (
            <div className="text-center py-12 text-content-secondary">
              Loading suppliers...
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-400">
              Failed to load suppliers. Please try again later.
            </div>
          ) : filteredSuppliers.length === 0 ? (
            <div className="text-center py-12 text-content-secondary">
              No suppliers found matching your criteria
            </div>
          ) : (
            filteredSuppliers.map((supplier) => (
              <SupplierCard key={supplier.id} supplier={supplier} />
            ))
          )}
        </div>

        {/* CTA moved to bottom */}
        <div className="mt-8">
          <SupplierCTA />
        </div>
      </div>
    </div>
  );
};

export default DirectoryPage;